import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import {
  Box,
  Typography,
  Chip,
  Stack,
  CircularProgress
} from '@mui/material';
import { API } from '../../api/api';

const FetalGrowthChartLocal = ({ babyName = "Baby", patientId }) => {
  const [scale, setScale] = useState('log');
  const [showGrowCurve, setShowGrowCurve] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [standard, setStandard] = useState('who');
  const [standardsList, setStandardsList] = useState([]);

  useEffect(() => {
    if (!patientId) return;
    const fetchStandards = async () => {
      const res = await API.get(`/v1/patients/${patientId}/growth-chart/standards`);
      if (res && res.success) {
        setStandardsList(res.data);
      }
    };
    fetchStandards();
  }, [patientId]);

  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      setLoading(true);
      const res = await API.get(`/v1/patients/${patientId}/growth-chart?standard=${standard}&scale=${scale}`);
      if (res && res.success) {
        setData(res.data);
      }
      setLoading(false);
    };
    fetchData();
  }, [patientId, standard]);

  if (loading) {
    return (
      <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) return null;

  const { referenceBands, patientTrend } = data;
  const isLog = scale === 'log';

  const percentilesKey = 'percentiles';

  const chartData = [];

  const maxWeek = Math.max(...referenceBands.gestationalAgeWeeks);

  referenceBands.gestationalAgeWeeks.forEach((week, index) => {
    const dataPoint = { week };

    if (referenceBands[percentilesKey]) {
      Object.keys(referenceBands[percentilesKey]).forEach(pct => {
        let val = referenceBands[percentilesKey][pct][index];
        if (isLog) val = Math.log(val);

        dataPoint[`p${pct}`] = val;
        if (week === maxWeek) {
          dataPoint[`label${pct}`] = `${pct}th`;
        }
      });
    }

    chartData.push(dataPoint);
  });

  if (showGrowCurve && data.growCurve && data.growCurve.curve) {
    const grow = data.growCurve.curve;
    grow.gestationalAgeWeeks.forEach((week, index) => {
      let existing = chartData.find(d => d.week === week);
      if (!existing) {
        existing = { week };
        chartData.push(existing);
      }

      let median = grow.median[index];
      let p10 = grow.p10[index];
      let p90 = grow.p90[index];

      if (isLog) {
        median = Math.log(median);
        p10 = Math.log(p10);
        p90 = Math.log(p90);
      }

      existing.growMedian = median;
      existing.growP10 = p10;
      existing.growP90 = p90;

      if (week === 40) {
        existing.labelGrowMedian = 'GROW';
      }
    });
  }

  patientTrend.forEach(pt => {
    if (pt.gestationalAgeWeeks === null) return;

    let val = pt.efwGrams;
    if (isLog) val = Math.log(val);

    let existing = chartData.find(d => d.week === pt.gestationalAgeWeeks);
    if (existing) {
      existing.patientValue = val;
      existing.patientPercentileLabel = pt.efwPercentile != null ? `${Math.round(pt.efwPercentile)}th` : '';
      existing.isPatientPoint = true;
    } else {
      chartData.push({
        week: pt.gestationalAgeWeeks,
        patientValue: val,
        patientPercentileLabel: pt.efwPercentile != null ? `${Math.round(pt.efwPercentile)}th` : '',
        isPatientPoint: true
      });
    }
  });

  chartData.sort((a, b) => a.week - b.week);

  const formatYAxis = (tickItem) => {
    const val = isLog ? Math.exp(tickItem) : tickItem;
    if (val >= 1000) return `${(val / 1000).toFixed(1)}kg`;
    return `${Math.round(val)}g`;
  };

  const pctColors = {
    '97.5': '#b3b3b3',
    '90': '#cccccc',
    '75': '#e6e6e6',
    '50': '#d9d9d9',
    '25': '#e6e6e6',
    '10': '#cccccc',
    '2.5': '#b3b3b3',
  };

  const percentiles = ['97.5', '90', '75', '50', '25', '10', '2.5'];
  const hasPercentiles = referenceBands[percentilesKey] != null;

  const renderLabel = (props, pct) => {
    const { x, y, value } = props;
    if (!value) return null;

    let targetY = y;
    if (isLog) {
      if (pct === '97.5') targetY = y - 36;
      else if (pct === '90') targetY = y - 24;
      else if (pct === '75') targetY = y - 12;
      else if (pct === '50') targetY = y;
      else if (pct === '25') targetY = y + 12;
      else if (pct === '10') targetY = y + 24;
      else if (pct === '2.5') targetY = y + 36;
    }

    return (
      <g>
        {Math.abs(targetY - y) > 2 && (
          <line x1={x} y1={y} x2={x + 6} y2={targetY} stroke="#d1d5db" strokeWidth={1} />
        )}
        <text x={x + 8} y={targetY} fill="#9ca3af" fontSize={11} alignmentBaseline="middle">
          {value}
        </text>
      </g>
    );
  };

  let yDomain = ['auto', 'auto'];
  if (data.yAxisDomain) {
    if (isLog) {
      yDomain = [Math.log(data.yAxisDomain.min || 70), Math.log(data.yAxisDomain.max || 4515)];
    } else {
      yDomain = [data.yAxisDomain.min || 70, data.yAxisDomain.max || 4515];
    }
  }

  const getStandardLabel = (key) => {
    if (key === 'who') return 'WHO';
    if (key === 'hadlock') return 'Hadlock';
    if (key === 'intergrowth') return 'INTERGROWTH';
    return key;
  };

  if (!data) {
    return null
  }

  return (
    <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, }}>

      <Stack direction="row" spacing={1} sx={{ mb: 4, flexWrap: 'wrap', gap: 1 }}>
        {standardsList.length > 0 ? standardsList.map((std) => {
          const isActive = std.key === standard;
          return (
            <Chip
              key={std.key}
              label={`${getStandardLabel(std.key)}${isActive ? ' ✓' : ''}`}
              onClick={() => setStandard(std.key)}
              variant={isActive ? 'filled' : 'outlined'}
              sx={{
                bgcolor: isActive ? '#e8f5e9' : 'transparent',
                color: isActive ? '#2e7d32' : 'default',
                borderColor: isActive ? '#2e7d32' : 'default',
                fontWeight: isActive ? 600 : 400,
                fontSize: "10px",
              }}
            />
          );
        }) : (
          <Chip
            label="WHO ✓"
            variant="filled"
            sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', borderColor: '#2e7d32', fontWeight: 600, fontSize: "10px", }}
          />
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Chip
          label={scale === 'log' ? 'Log scale (recommended)' : 'Log scale'}
          onClick={() => setScale('log')}
          variant={scale === 'log' ? 'filled' : 'outlined'}
          sx={{
            borderColor: '#f59e0b',
            color: scale === 'log' ? '#b45309' : '#d97706',
            bgcolor: scale === 'log' ? '#fef3c7' : 'transparent',
            fontWeight: scale === 'log' ? 600 : 400,
            fontSize: "10px",
          }}
        />
        <Chip
          label="Linear scale"
          onClick={() => setScale('linear')}
          variant={scale === 'linear' ? 'filled' : 'outlined'}
          sx={{
            fontWeight: scale === 'linear' ? 600 : 400,
            borderColor: scale === 'linear' ? '#f59e0b' : 'default',
            color: scale === 'linear' ? '#b45309' : 'default',
            bgcolor: scale === 'linear' ? '#fef3c7' : 'transparent',
            fontSize: "10px",
          }}
        />
        <Chip
          label="Show GROW curve"
          onClick={() => setShowGrowCurve(!showGrowCurve)}
          variant={showGrowCurve ? 'filled' : 'outlined'}
          sx={{
            color: showGrowCurve ? '#312e81' : '#4f46e5',
            bgcolor: showGrowCurve ? '#e0e7ff' : 'transparent',
            borderColor: '#c7d2fe',
            fontWeight: showGrowCurve ? 600 : 400,
            fontSize: "10px",
          }}
        />
      </Stack>

      {/* Chart Area */}
      <Box sx={{ width: '100%', height: 250, position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 50, right: 80, left: 10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />

            <XAxis
              dataKey="week"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(val) => `${val}w`}
              axisLine={false}
              tickLine={false}
              tick={{
                fill: "#000",
                fontSize: "8px",
                fontWeight: "bold",
              }}
              dy={15}
            />

            <YAxis
              domain={yDomain}
              tickFormatter={formatYAxis}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: '8px', fontWeight: 'bold', fill: "#000", }}
              dx={-10}
              width={60}
            />

            <Tooltip
              formatter={(value, name) => {
                if (name === 'patientValue') {
                  const rawGrams = isLog ? Math.exp(value) : value;
                  return [`${Math.round(rawGrams)}g`, 'Estimated Fetal Weight'];
                }
                const rawGrams = isLog ? Math.exp(value) : value;
                return [`${Math.round(rawGrams)}g`, `Percentile ${name.replace('p', '')}`];
              }}
              labelFormatter={(label) => `Week ${label}`}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
            />

            {/* Reference bands */}
            {hasPercentiles && percentiles.map(pct => (
              <Line
                key={pct}
                type="monotone"
                dataKey={`p${pct}`}
                stroke={pctColors[pct]}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
                connectNulls
              >
                <LabelList
                  dataKey={`label${pct}`}
                  content={(props) => renderLabel(props, pct)}
                />
              </Line>
            ))}

            {/* GROW Curve */}
            {showGrowCurve && data.growCurve && (
              <>
                <Line
                  type="monotone"
                  dataKey="growMedian"
                  stroke="#6A5CAE"
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls
                >
                  <LabelList
                    dataKey="labelGrowMedian"
                    position="right"
                    style={{ fill: '#6A5CAE', fontSize: 12, fontWeight: 700 }}
                    offset={5}
                  />
                </Line>
                <Line
                  type="monotone"
                  dataKey="growP10"
                  stroke="#6A5CAE"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="growP90"
                  stroke="#6A5CAE"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              </>
            )}

            {/* Patient trend line */}
            <Line
              type="monotone"
              dataKey="patientValue"
              stroke="#b47833"
              strokeWidth={3}
              dot={{ stroke: '#b47833', strokeWidth: 2, r: 5, fill: '#b47833' }}
              activeDot={{ r: 7 }}
              connectNulls
            >
              <LabelList
                dataKey="patientPercentileLabel"
                position="bottom"
                offset={10}
                style={{ fill: '#b47833', fontWeight: 600, fontSize: 13 }}
              />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Signals Table */}
      {data.signals && data.signals.length > 0 && (
        <Box sx={{ mt: 3, borderTop: '1px solid #eaeaea', borderLeft: '1px solid #eaeaea', borderRight: '1px solid #eaeaea', borderRadius: 0, overflow: 'hidden' }}>
          {data.signals.map((signal, idx) => {
            const getToneColor = (tone) => {
              switch (tone) {
                case 'sage': return '#507A5D';
                case 'amber': return '#A67C00';
                case 'rust': return '#A94442';
                case 'slate': return '#6B7280';
                default: return '#6B7280';
              }
            };
            const color = getToneColor(signal.tone);

            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  px: 2,
                  borderBottom: '1px solid #eaeaea'
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                  <Typography sx={{ fontSize: '13px', color: '#1f2937' }}>
                    {signal.label}
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '13px', color: color, fontFamily: 'monospace', fontWeight: 600 }}>
                  {signal.value}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default FetalGrowthChartLocal;
